import React, { RefObject } from "react"
import type { DefaultDocumentsCommandsPanelTourTargets } from "../../commandsPanels/DefaultDocumentsCommandsPanelV3"
import { TourStep } from "../Tour"
import * as FFIcons from "../../../libs/corporate/icons/outlined/other/FileAndFolderCollection"
import * as ActionIcons from "../../../libs/corporate/icons/outlined/editor/ActionCollection"
import { LeftIcon, RightIcon } from "../../../libs/corporate/icons/outlined/directions/ArrowsInCircleCollection"
import * as MailIcons from "../../../libs/corporate/icons/outlined/other/MailCollection"
import * as MainIcons from "../../../libs/corporate/icons/outlined/action/MainCollection"
import * as RotationIcons from "../../../libs/corporate/icons/outlined/directions/RotationCollection"
import { KizBarcodeIcon } from "../../../libs/corporate/icons/outlined/kiz/KizBarcodeCollection"
import { returnMarkingStatusIcon, returnStatusIcon } from "../../../system/functions/returnStatusIcon"

export interface InvoicePluginViewTourRefs {
    grid: RefObject<HTMLDivElement>
    detailsTabs: RefObject<HTMLDivElement>
}

export const getInvoiceDocumentCommandsPanelTourSteps = (targets: DefaultDocumentsCommandsPanelTourTargets): TourStep[] => [
    {
        target: () => targets.commandsPanel,
        description: (
            <div>
                Панель содержит стандартные команды управления:
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <FFIcons.FilePlusIcon /> Добавить запись
                </div>
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <ActionIcons.HighlightIcon /> Редактировать запись
                </div>
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <RightIcon /> Провести документ
                </div>
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <LeftIcon /> Отменить документ
                </div>
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <ActionIcons.CopyIcon /> Копировать
                </div>
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <MailIcons.PrinterIcon /> Печать
                </div>
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <MainIcons.Delete2Icon /> Удалить
                </div>
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <MainIcons.DeleteIcon /> Восстановить
                </div>
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <MainIcons.SearchIconPanel /> Поиск
                </div>
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <RotationIcons.RotationRightIcon /> Обновить
                </div>
            </div>
        ),
        placement: "bottom",
    },
    {
        target: () => targets.datePeriodPanel,
        description: `Фильтр "Период" со встроенным календарём: по умолчанию показывает документы за последние 2 недели`,
        placement: "bottom",
    },
    {
        target: () => targets.statusSelect,
        description: (
            <div>
                Фильтрация по статусу, показывает накладные:
                <ul>
                    <li>Все</li>
                    <li>Сохранённые</li>
                    <li>Отработанные</li>
                    <li>Обеспеченные</li>
                    <li>Удалённые</li>
                </ul>
            </div>
        ),
        placement: "bottom",
    },
    {
        target: () => targets.searchPanel,
        description: (
            <div>
                Кнопка "Поиск" фильтрует документы по:
                <ul>
                    <li>Номеру</li>
                    <li>Поставщику</li>
                    <li>Складу</li>
                    <li>Дате</li>
                    <li>Статусу</li>
                </ul>
            </div>
        ),
        placement: "bottom",
    },
    {
        target: () => targets.addEditCommands,
        description: (
            <div>
                Для создание приходной накладной нажмите кнопку:
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <FFIcons.FilePlusIcon /> Добавить запись
                </div>
                При нажатии:
                <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                    <ActionIcons.HighlightIcon /> Редактировать запись
                </div>
                открывается форма выбранной накладной для редактирования. По умолчанию установлены контрагент и склад, для которых была сформирована последняя ПН
            </div>
        ),
        placement: "bottom",
    },
]

export const getInvoicePluginViewTourSteps = (refs: InvoicePluginViewTourRefs, documentCommandsPanelTourSteps: TourStep[]): TourStep[] => {
    const documentPanelFirstSteps = documentCommandsPanelTourSteps.slice(0, 4)
    const addEditStep = documentCommandsPanelTourSteps[4]

    return [
        ...documentPanelFirstSteps,
        {
            target: () => refs.grid.current,
            description: (
                <div>
                    Это таблица со списком "Приходных накладных".
                    <div>
                        В колонке "СД", обозначение иконок:
                        <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                            {returnStatusIcon("draft" as any)} Черновик
                        </div>
                        <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                            {returnStatusIcon("proc" as any)} Отработан
                        </div>
                        <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                            {returnStatusIcon("del" as any)} Удалён
                        </div>
                    </div>
                    <div>
                        В колонке "СМ", обозначение иконок:
                        <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                            <span style={{width: "25px"}}>{returnMarkingStatusIcon("noKizs")}</span> Нет КИЗ
                        </div>
                        <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                            <span style={{width: "25px"}}>{returnMarkingStatusIcon("allSave")}</span> Есть КИЗ - сохранен
                        </div>
                        <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                            <span style={{width: "25px"}}>{returnMarkingStatusIcon("allSuccess")}</span> Есть КИЗ - готов
                        </div>
                        <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                            <span style={{width: "25px"}}>{returnMarkingStatusIcon("allFailed")}</span> Есть КИЗ - не готов
                        </div>
                        <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                            <span style={{width: "25px"}}>{returnMarkingStatusIcon("partialSuccess")}</span> Есть КИЗ - в процессе
                        </div>
                    </div>
                </div>
            ),
            placement: "top",
        },
        {
            target: () => refs.detailsTabs.current,
            description: (
                <div>
                    Панель отображает детализацию выбранного документа:
                    <ul>
                        <li>"Позиции": список товаров в накладной</li>
                        <li>"КИЗ": статусы маркировки + переход в журнал КИЗ</li>
                        <li>"Короб" / "Короб ГИС МТ": данные об агрегации</li>
                    </ul>
                </div>
            ),
            placement: "top",
        },
        {
            target: () => refs.detailsTabs.current,
            description: (
                <div>
                    Во вкладке "Позиции":
                    <div style={{display: "flex", alignItems: "center", margin: "5px", gap: "10px"}}>
                        <span style={{width: "25px"}}><KizBarcodeIcon /></span> Кнопка КИЗ, открывает окно для быстрого редактирования КИЗ
                    </div>
                </div>
            ),
            placement: "top",
        },
        ...(addEditStep ? [addEditStep] : []),
    ]
}
